import { AlertCalloutToaster } from "@/shared/alerts";
import AccessDeniedPage from "@/shared/components/error-pages/access-denied-page";
import { Auth0Provider } from "@auth0/auth0-react";
import { TooltipProvider } from "@spz/ui/components/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import envService from "./env.service";
import { controlResultLoader } from "./features/control-results/loaders/control-result.loader";
import AccountDeactivated from "./pages/account-deactivated";
import ControlResults from "./pages/control-results";
import Controls from "./pages/controls";
import Dashboard from "./pages/dashboard";
import ErrorPage from "./pages/error-page";
import LogoutSuccess from "./pages/logout-success";
import CompaniesTab from "./pages/management-settings/companies-tab";
import CompanyGroupsTab from "./pages/management-settings/company-groups-tab";
import ManagementSettings from "./pages/management-settings/management-settings";
import { OrganizationSettings } from "./pages/management-settings/organization-settings-tab";
import RolesTab from "./pages/management-settings/roles-tab";
import UsersGroupsTab from "./pages/management-settings/user-groups-tab";
import UsersTab from "./pages/management-settings/users-tab";
import Onboarding from "./pages/onboarding";
import ReportDownloadErrorPage from "./pages/report-download-error-page";
import ReportDownloadPage from "./pages/report-download-page";
import { queryConfig } from "./reactQueryInstance";
import { createRepositories } from "./repositories/repository-factory";
import { AuthenticatedLayout } from "./shared/components/layout/authenticated-layout";
import { GuestLayout } from "./shared/components/layout/guest-layout";
import { RootError } from "./shared/components/root-error";
import { RepositoryProvider } from "./shared/contexts/repository-context";
import { protectedLoader } from "./shared/loaders/protected.loader";

// Export query client for use in loaders
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

// Route metadata (used for navigation, breadcrumbs, translations)
interface Route {
  path: string;
  translationKey: string;
}

type RouteKey =
  | "root"
  | "dashboard"
  | "controls"
  | "deactivated"
  | "onboarding"
  | "controlResult";

export const routes: Record<RouteKey, Route> = {
  root: {
    path: "/",
    translationKey: "root",
  },
  dashboard: {
    path: "/dashboard",
    translationKey: "dashboard",
  },
  controls: {
    path: "/controls",
    translationKey: "controls_list_title",
  },
  onboarding: {
    path: "/onboarding",
    translationKey: "onboarding",
  },
  deactivated: {
    path: "/deactivated",
    translationKey: "account_disabled_title",
  },
  controlResult: {
    path: "/controls/:controlId/results",
    translationKey: "control_results_list_title",
  },
} as const;

// Complete paths constant
export const paths = {
  root: "/",
  ...Object.fromEntries(
    Object.entries(routes).map(([key, route]) => [key, route.path]),
  ),
} as const;

export type RoutePath = (typeof paths)[keyof typeof paths];

// Router definition
const router = createBrowserRouter([
  {
    element: <AuthenticatedLayout />,
    loader: protectedLoader,
    shouldRevalidate: () => false,
    errorElement: <RootError />,
    children: [
      {
        index: true,
        element: <Navigate to={routes.controls.path + location.search} />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
        errorElement: <ErrorPage />,
      },
      {
        path: "controls",
        element: <Controls />,
        errorElement: <ErrorPage />,
      },
      {
        path: "controls/:controlId/results",
        element: <ControlResults />,
        errorElement: <ErrorPage />,
        loader: controlResultLoader,
      },
      {
        path: "/management-settings",
        element: <ManagementSettings />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "organization-settings",
            element: <OrganizationSettings />,
          },
          { path: "users", element: <UsersTab /> },
          { path: "users-groups", element: <UsersGroupsTab /> },
          { path: "roles", element: <RolesTab /> },
          { path: "company-groups", element: <CompanyGroupsTab /> },
          { path: "companies", element: <CompaniesTab /> },
          {
            index: true,
            element: <Navigate to="organization-settings" replace />,
          },
        ],
      },
    ],
  },
  {
    element: <GuestLayout />,
    loader: protectedLoader,
    shouldRevalidate: () => false,
    errorElement: <ErrorPage />,
    children: [
      { path: "onboarding", element: <Onboarding /> },
      {
        path: "reports/:reportId/download",
        element: <ReportDownloadPage />,
        errorElement: <ReportDownloadErrorPage />,
      },
    ],
  },
  {
    element: <GuestLayout />,
    children: [
      {
        path: "deactivated",
        element: <AccountDeactivated />,
      },
      {
        path: "access-denied",
        element: <AccessDeniedPage />,
      },
      {
        path: "logout-success",
        element: <LogoutSuccess />,
      },
    ],
  },
]);

const repositories = createRepositories();

// Main App component
export const App = () => (
  <Auth0Provider
    domain={envService.env.auth0.domain}
    clientId={envService.env.auth0.clientId}
    authorizationParams={{
      audience: envService.env.auth0.audience,
      redirect_uri: envService.env.auth0.callbackUrl,
    }}
    cacheLocation="localstorage"
  >
    <QueryClientProvider client={queryClient}>
      <RepositoryProvider repositories={repositories}>
        <TooltipProvider delayDuration={500} skipDelayDuration={0}>
          <NuqsAdapter>
            <RouterProvider router={router} />
            {envService.env.envName === "development" && <ReactQueryDevtools />}
          </NuqsAdapter>
        </TooltipProvider>
      </RepositoryProvider>
    </QueryClientProvider>
    <AlertCalloutToaster />
  </Auth0Provider>
);
